import React, { useEffect, useState } from "react";
import api, { API } from "@/lib/api";
import { downloadBlob } from "@/lib/download";
import PageHeader from "@/components/common/PageHeader";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { fmtINR } from "@/lib/format";
import { Loader2, Play, Download, FileSpreadsheet, FileText, ChevronRight, ChevronDown, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { useDrillDown, DrillProvider, useOptionalDrill } from "@/components/reports/DrillDownDrawer";

const today = () => new Date().toISOString().slice(0, 10);
const fyStart = () => { const t = new Date(); const y = t.getMonth() >= 3 ? t.getFullYear() : t.getFullYear() - 1; return `${y}-04-01`; };
const currentMonth = () => new Date().toISOString().slice(0, 7);

// Colour classes for the 13-row structure
const TONE_CLS = {
  positive: "text-emerald-500",
  negative: "text-red-500",
  subtotal: "font-bold text-blue-500 bg-blue-500/10",
};
const ROW_KEY_TONE = {
  direct_income: "positive", indirect_income: "positive",
  direct_expenses: "negative", indirect_expenses: "negative",
  apportioned_expenses: "negative", depreciation_amortization: "negative",
  interest: "negative", taxes: "negative",
};

function PnlTable({ data, testid, dimension, period }) {
  const [collapsed, setCollapsed] = useState({}); // key → true
  const drill = useOptionalDrill();
  if (!data) return null;
  const columns = data.columns || [];
  const openCell = async (row, col, val) => {
    if (col.id === "__total") return;
    // Only "collapsible" (raw) rows have contributing accounts; subtotals derive from them
    if (!row.collapsible) return;
    if (Math.abs(val) < 0.005) return;
    try {
      const r = await api.get("/reports/mis/segment-details", {
        params: { dimension, row_key: row.key, segment_id: col.id, from_date: period?.from, to_date: period?.to },
      });
      const accs = (r.data.accounts || []).map((a) => ({
        account_id: a.account_id, code: a.code, name: a.name,
        amount: a.amount, entry_count: a.entry_count,
      }));
      drill.openSubcategory(`${col.name} · ${row.label} · ${fmtINR(val)}`, accs);
    } catch (e) { /* silent */ }
  };
  return (
    <div className="card-sharp overflow-x-auto" data-testid={testid}>
      <table className="w-full text-sm">
        <thead className="border-b">
          <tr>
            <th className="text-left py-2 pl-3 text-[10px] font-mono uppercase text-muted-foreground">Line Item</th>
            {columns.map(c => (
              <th key={c.id} className="text-right pr-3 text-[10px] font-mono uppercase text-muted-foreground">
                {c.label || c.name}
                {c.is_head_office && <span className="ml-1 text-accent">·HO</span>}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.rows.map(row => {
            const isSub = row.tone === "subtotal";
            const isCollapsible = row.collapsible;
            const isOpen = !collapsed[row.key];
            return (
              <tr key={row.key} className={`border-b ${TONE_CLS[row.tone] || ""}`} data-testid={`pnl-row-${row.key}`}>
                <td className="py-2 pl-3">
                  <div className="flex items-center gap-2">
                    {isCollapsible ? (
                      <button onClick={() => setCollapsed({ ...collapsed, [row.key]: isOpen })} className="text-muted-foreground hover:text-foreground">
                        {isOpen ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
                      </button>
                    ) : <span className="w-3.5" />}
                    <span className={isSub ? "uppercase tracking-wide text-xs" : ""}>{row.label}</span>
                  </div>
                </td>
                {columns.map(c => {
                  const v = row.values[c.id] ?? 0;
                  const canDrill = isCollapsible && c.id !== "__total" && Math.abs(v) > 0.005;
                  return (
                    <td
                      key={c.id}
                      onClick={canDrill ? () => openCell(row, c, v) : undefined}
                      className={`text-right pr-3 font-mono tabular-nums ${isSub ? "font-bold text-blue-500" : ""} ${!isSub && v < 0 ? "text-red-500" : ""} ${canDrill ? "cursor-pointer hover:bg-muted/40 hover:underline decoration-dotted underline-offset-2" : ""}`}
                      data-testid={`pnl-cell-${row.key}-${c.id}`}
                    >
                      {v === 0 ? "—" : fmtINR(v)}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function SegmentPnlPane({ dimension }) {
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [apportion, setApportion] = useState(true); const [basis, setBasis] = useState("revenue");
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/mis/full-pnl", { params: { dimension, from_date: fr, to_date: to, apportion_ho: apportion, basis } });
      setData(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, [dimension]);
  return (
    <div className="space-y-4" data-testid={`mis-${dimension}`}>
      <div className="card-sharp p-4 flex gap-3 items-end flex-wrap">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid={`${dimension}-from`} /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid={`${dimension}-to`} /></div>
        <label className="flex items-center gap-2 border border-border px-3 py-2 cursor-pointer">
          <Switch checked={apportion} onCheckedChange={setApportion} data-testid={`${dimension}-apportion`} />
          <span className="text-xs">Apportion HO</span>
        </label>
        <div className="min-w-[140px]"><Label>Basis</Label>
          <Select value={basis} onValueChange={setBasis} disabled={!apportion}>
            <SelectTrigger className="mt-1" data-testid={`${dimension}-basis`}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="revenue">Revenue Share</SelectItem>
              <SelectItem value="equal">Equal Split</SelectItem>
              <SelectItem value="none">None</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={run} disabled={busy} className="ml-auto" data-testid={`${dimension}-run`}>
          {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Running…</> : <><Play className="w-4 h-4 mr-2" /> Refresh</>}
        </Button>
      </div>
      <PnlTable data={data} testid={`${dimension}-pnl-table`} dimension={dimension} period={{ from: fr, to }} />
    </div>
  );
}

function GlobalPnlPane() {
  const [month, setMonth] = useState(currentMonth());
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/mis/global-pnl", { params: { reporting_month: month } });
      setData(r.data);
    } finally { setBusy(false); }
  };
  useEffect(() => { run(); /* eslint-disable-next-line */ }, []);
  return (
    <div className="space-y-4" data-testid="mis-global">
      <div className="card-sharp p-4 flex gap-3 items-end">
        <div><Label>Reporting Month</Label><Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="mt-1" data-testid="global-month" /></div>
        <Button onClick={run} disabled={busy} data-testid="global-run">{busy ? "Running…" : "Refresh"}</Button>
      </div>
      <PnlTable data={data} testid="global-pnl-table" />
    </div>
  );
}

async function download(path, filename) {
  const ok = await downloadBlob(path, filename);
  if (ok) toast.success(`Downloaded ${filename}`);
}

function MISPackPane() {
  const [fr, setFr] = useState(fyStart()); const [to, setTo] = useState(today());
  const [rm, setRm] = useState(currentMonth());
  const [data, setData] = useState(null); const [busy, setBusy] = useState(false);
  const preview = async () => {
    setBusy(true);
    try {
      const r = await api.get("/reports/mis/pack", { params: { from_date: fr, to_date: to, reporting_month: rm } });
      setData(r.data);
      toast.success("MIS Pack generated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    setBusy(false);
  };
  useEffect(() => { preview(); /* eslint-disable-next-line */ }, []);
  const qs = `from_date=${fr}&to_date=${to}&reporting_month=${rm}`;
  return (
    <div className="space-y-4" data-testid="mis-pack">
      <div className="card-sharp p-4 flex gap-3 items-end flex-wrap">
        <div><Label>From</Label><Input type="date" value={fr} onChange={e => setFr(e.target.value)} className="mt-1" data-testid="pack-from" /></div>
        <div><Label>To</Label><Input type="date" value={to} onChange={e => setTo(e.target.value)} className="mt-1" data-testid="pack-to" /></div>
        <div><Label>Reporting Month</Label><Input type="month" value={rm} onChange={e => setRm(e.target.value)} className="mt-1" data-testid="pack-rm" /></div>
        <Button onClick={preview} disabled={busy} data-testid="pack-generate">{busy ? "Generating…" : "Generate"}</Button>
        <Button variant="outline" onClick={() => download(`/reports/mis/pack.xlsx?${qs}`, `mis-pack_${fr}_to_${to}.xlsx`)} data-testid="pack-xlsx">
          <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel
        </Button>
        <Button variant="outline" onClick={() => download(`/reports/mis/pack.pdf?${qs}`, `mis-pack_${fr}_to_${to}.pdf`)} data-testid="pack-pdf">
          <FileText className="w-4 h-4 mr-2" /> PDF
        </Button>
      </div>

      {data && (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
          {/* 1. Business EBITDA */}
          <div className="card-sharp p-3" data-testid="pack-business">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">1. Business Segment EBITDA</div>
            <PnlTable data={data.business_pnl} />
          </div>
          {/* 2. Geo EBITDA */}
          <div className="card-sharp p-3" data-testid="pack-geo">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">2. Geographical Segment EBITDA</div>
            <PnlTable data={data.geo_pnl} />
          </div>
          {/* 3. Global EBITDA */}
          <div className="card-sharp p-3 xl:col-span-2" data-testid="pack-global">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">3. Global EBITDA — Reporting Month · Prev Month · YTD · Prev Year Same Month</div>
            <PnlTable data={data.global_pnl} />
          </div>

          {/* 4. Top products */}
          <div className="card-sharp p-3" data-testid="pack-top-products">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">4. Top 10 Products (by gross margin)</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-1">Product</th><th className="text-right">Qty</th><th className="text-right">Revenue</th><th className="text-right">GM</th><th className="text-right">%</th></tr></thead>
              <tbody>
                {(data.products?.rows || []).map((p, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-1">{p.name}</td>
                    <td className="text-right font-mono">{p.qty_sold.toLocaleString("en-IN")}</td>
                    <td className="text-right font-mono">{fmtINR(p.revenue)}</td>
                    <td className={`text-right font-mono ${p.gross_margin >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtINR(p.gross_margin)}</td>
                    <td className="text-right font-mono">{p.margin_pct.toFixed(1)}%</td>
                  </tr>
                ))}
                {(!data.products?.rows || data.products.rows.length === 0) && <tr><td colSpan={5} className="text-center py-4 text-muted-foreground text-xs">No sales in period</td></tr>}
              </tbody>
            </table>
          </div>

          {/* 5. Top customers */}
          <div className="card-sharp p-3" data-testid="pack-top-customers">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">5. Top 10 Customers (by revenue)</div>
            <table className="w-full text-sm">
              <thead><tr className="border-b"><th className="text-left py-1">Customer</th><th className="text-right">Invoices</th><th className="text-right">Revenue</th><th className="text-right">Outstanding</th></tr></thead>
              <tbody>
                {(data.customers?.rows || []).map((c, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-1">{c.name}</td>
                    <td className="text-right font-mono">{c.invoices}</td>
                    <td className="text-right font-mono text-emerald-500">{fmtINR(c.revenue)}</td>
                    <td className={`text-right font-mono ${c.outstanding > 0 ? "text-orange-500" : ""}`}>{fmtINR(c.outstanding)}</td>
                  </tr>
                ))}
                {(!data.customers?.rows || data.customers.rows.length === 0) && <tr><td colSpan={4} className="text-center py-4 text-muted-foreground text-xs">No sales</td></tr>}
              </tbody>
            </table>
          </div>

          {/* 6. AR aging */}
          <div className="card-sharp p-3" data-testid="pack-ar">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">6. AR Ageing</div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {Object.entries(data.ar_aging?.buckets || {}).map(([b, v]) => (
                <div key={b} className={`border p-2 ${v > 0 ? "border-orange-500/30" : "border-border"}`}>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">{b}</div>
                  <div className={`font-mono ${v > 0 ? "text-orange-500" : ""}`}>{fmtINR(v)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 7. AP aging */}
          <div className="card-sharp p-3" data-testid="pack-ap">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">7. AP Ageing</div>
            <div className="grid grid-cols-4 gap-2 mb-3">
              {Object.entries(data.ap_aging?.buckets || {}).map(([b, v]) => (
                <div key={b} className={`border p-2 ${v > 0 ? "border-red-500/30" : "border-border"}`}>
                  <div className="text-[10px] font-mono uppercase text-muted-foreground">{b}</div>
                  <div className={`font-mono ${v > 0 ? "text-red-500" : ""}`}>{fmtINR(v)}</div>
                </div>
              ))}
            </div>
          </div>

          {/* 8. AI Insights */}
          <div className="card-sharp p-3 xl:col-span-2 border-l-4 border-l-accent" data-testid="pack-insights">
            <div className="text-[10px] font-mono uppercase text-accent mb-2 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> 8. AI Insights
            </div>
            <div className="whitespace-pre-line text-sm">{data.insights || "(insights unavailable)"}</div>
          </div>

          {/* 9. Compliance */}
          <div className="card-sharp p-3" data-testid="pack-compliance">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">9. Compliance Tracker</div>
            <div className="mb-3 flex gap-4 text-xs">
              <span className="text-red-500 font-mono">Overdue: {data.compliance?.counts?.overdue || 0}</span>
              <span className="text-orange-500 font-mono">Next 30 days: {data.compliance?.counts?.upcoming_30d || 0}</span>
              <span className="text-muted-foreground font-mono">Total: {data.compliance?.counts?.total || 0}</span>
            </div>
            <table className="w-full text-xs">
              <thead><tr className="border-b"><th className="text-left py-1">Filing</th><th className="text-left">Period</th><th className="text-left">Due</th><th className="text-left">Status</th></tr></thead>
              <tbody>
                {(data.compliance?.next_due || []).concat(data.compliance?.overdue || []).slice(0, 10).map((e, i) => (
                  <tr key={i} className="border-b">
                    <td className="py-1">{e.kind}</td>
                    <td>{e.period}</td>
                    <td className="font-mono">{e.due_date}</td>
                    <td className={`uppercase font-mono ${e.status === "overdue" ? "text-red-500 font-bold" : "text-emerald-500"}`}>{e.status}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* 10. Cash flow */}
          <div className="card-sharp p-3" data-testid="pack-cashflow">
            <div className="text-[10px] font-mono uppercase text-accent mb-2">10. Cash Flow Position</div>
            <table className="w-full text-sm">
              <tbody>
                {[["operating", "Cash from Operations"], ["investing", "Cash from Investing"], ["financing", "Cash from Financing"]].map(([k, l]) => (
                  <tr key={k} className="border-b">
                    <td className="py-1.5">{l}</td>
                    <td className={`text-right font-mono ${(data.cash_flow?.[k] || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtINR(data.cash_flow?.[k] || 0)}</td>
                  </tr>
                ))}
                <tr className="border-t-2 font-bold">
                  <td className="py-2">Net Change in Cash</td>
                  <td className={`text-right font-mono text-base ${(data.cash_flow?.net_change || 0) >= 0 ? "text-emerald-500" : "text-red-500"}`}>{fmtINR(data.cash_flow?.net_change || 0)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default function SegmentMIS() {
  const drill = useDrillDown();
  return (
    <DrillProvider drill={drill}>
      <div className="p-6 lg:p-8" data-testid="segment-mis-page">
        <PageHeader eyebrow="MIS · Profitability" title="Comprehensive Management Information System"
          description="Full-house P&L (Gross Margin → EBITDA → EBIT → EBT → EAT) by Business Segment, Geographical Segment or Global. Click any raw-row cell (Direct/Indirect Income, Direct/Indirect Expenses, D&A, Interest, Taxes) to drill into contributing accounts → ledger → voucher." />
        <Tabs defaultValue="pack">
          <TabsList className="rounded-sm h-auto flex-wrap">
            <TabsTrigger value="pack" className="rounded-sm" data-testid="tab-mis-pack">MIS Pack (Full)</TabsTrigger>
            <TabsTrigger value="business" className="rounded-sm" data-testid="tab-mis-business">Business Segment P&L</TabsTrigger>
            <TabsTrigger value="geo" className="rounded-sm" data-testid="tab-mis-geo">Geographical P&L</TabsTrigger>
            <TabsTrigger value="global" className="rounded-sm" data-testid="tab-mis-global">Global P&L</TabsTrigger>
          </TabsList>
          <TabsContent value="pack" className="mt-4"><MISPackPane /></TabsContent>
          <TabsContent value="business" className="mt-4"><SegmentPnlPane dimension="business" /></TabsContent>
          <TabsContent value="geo" className="mt-4"><SegmentPnlPane dimension="geo" /></TabsContent>
          <TabsContent value="global" className="mt-4"><GlobalPnlPane /></TabsContent>
        </Tabs>
      </div>
    </DrillProvider>
  );
}
